import React, { useState, SyntheticEvent } from 'react'
import { ButtonGroup, InputGroup, Position, Dialog } from '@blueprintjs/core'
import { Flex, IconButton, PrettyDate, Popover, Toaster } from '@ui'

import {
  NewPlanGroupDialog,
  NewPlanDialog,
  DeletePlanDialog,
  ClonePlanDialog,
  PromotePlanDialog,
  DeletePlanGroupDialog,
} from './dialogs'
import api, { IScenarioGroup, IAssignmentPlan } from '@api'
import useScenarioGroups from './useScenarioGroups'
import useDialogHandler from './useDialogHandler'
import { redirectTo } from './utils'
import css from './apWidget.module.scss'
import { Tooltip2 } from '@blueprintjs/popover2'
import {
  Button as IButton,
  TreeList,
  TreeListItem,
  Icon,
  Tag,
  Popover as IPopover,
  Divider,
} from '@runeffective/evo-design-system'
import { DateUtil } from '@utils'

interface APWidgetProps {
  value: string | null
  readonly?: boolean
  onChange: (ap: IAssignmentPlan) => void
}

enum Actions {
  NEW_SCENARIO = 'New Scenario',
  RENAME = 'Rename',
  DUPLICATE = 'Duplicate',
  COMMIT = 'Commit',
  DELETE = 'Delete',
}

enum Icons {
  ADD = 'add',
  PENCIL = 'pencil',
  COPY = 'copy',
  PLAY = 'play',
  TRASH = 'trash',
  CALENDAR = 'calendar',
}

export default function APWidget({ value, readonly, onChange }: APWidgetProps) {
  const { loading, groups, deleteGroup, deletePlan, updatePlan } = useScenarioGroups()
  const { activeDialog, data, openDialog, closeDialog } = useDialogHandler()
  const [renaming, setRenaming] = useState<string | null>(null)
  const [sldPlan, setSldPlan] = useState<any>(null)
  const primaryPlan = getPrimaryPlan(groups)
  let selectedPlan = value ? getPlanById(groups, value) : primaryPlan
  const [isPopoverOpen, setIsPopoverOpen] = useState(false)

  // Redirect to primary plan if selected plan not found
  if (!selectedPlan && !loading) {
    redirectTo(primaryPlan)
    return null
  }

  const renderActionsPopover = (triggerElement: React.ReactElement, value: any) => {
    let actionsArr: any = [
      {
        icon: Icons.PENCIL,
        title: Actions.RENAME,
        onclick: () => {
          setSldPlan(value)
          setRenaming(value?.id)
        },
      },
      {
        icon: Icons.COPY,
        title: Actions.DUPLICATE,
        onclick: () => openDialog('ClonePlanDialog', value),
      },
      {
        icon: Icons.PLAY,
        title: Actions.COMMIT,
        onclick: () => openDialog('PromotePlanDialog', value),
      },
      {
        icon: Icons.TRASH,
        title: Actions.DELETE,
        onclick: () => openDialog('DeletePlanDialog', value),
      },
    ]
    if (value.assignmentPlans) {
      actionsArr = [
        {
          icon: Icons.ADD,
          title: Actions.NEW_SCENARIO,
          onclick: () => openDialog('AddPlanDialog', value),
        },
        {
          icon: Icons.TRASH,
          title: Actions.DELETE,
          onclick: () => openDialog('DeletePlanGroupDialog', value),
        },
      ]
    }
    if (value.parentAssignmentPlanId && !value.assignmentPlans) {
      actionsArr = actionsArr.filter((value: { icon: Icons; title: Actions }) => {
        if (value.title !== Actions.DUPLICATE) return value
      })
    }

    return (
      <IPopover triggerElement={triggerElement} closeIcon={false}>
        <IPopover.Content>
          {actionsArr?.map((value: { icon: Icons; title: Actions; onclick: () => void }) => {
            return (
              <IButton
                style={{ width: '100%' }}
                key={value.title}
                aria-label={value.title}
                layout="borderless"
                size="small"
                onClick={value.onclick}
                startIcon={<Icon iconName={value.icon} iconStyle="fal" size="sm" />}>
                {value.title}
              </IButton>
            )
          })}
        </IPopover.Content>
      </IPopover>
    )
  }

  const menuItems = groups.map((group: IScenarioGroup) => {
    if (group.assignmentPlans[0] && group.assignmentPlans[0].name === 'Current') {
      const plan = group.assignmentPlans[0]
      return (
        <TreeListItem
          leftIcon={<Icon iconName="folder-closed" iconStyle="fal" size="sm" />}
          spaceVariant="spaced"
          nodeId={plan.id}
          key={plan.id}
          title="Now"
          onClick={() => onChange(plan)}
        />
      )
    } else {
      return (
        <>
          <TreeListItem
            leftIcon={<Icon iconName="folder-closed" iconStyle="fal" size="sm" />}
            spaceVariant="spaced"
            nodeId={group.id}
            key={group.id}
            title={DateUtil(group.deployAt).format('MMM d, yyyy')}
            tag={<Tag title={group.assignmentPlans.length.toString()} variant="Informational" />}
            onClick={() => setSldPlan(group)}
            rightIcon={
              !readonly
                ? renderActionsPopover(
                    <Icon
                      aria-label="more_options"
                      iconName="ellipsis-vertical"
                      iconStyle="fal"
                      size="sm"
                    />,
                    group
                  )
                : undefined
            }>
            {group.assignmentPlans.map((value) => {
              return (
                <TreeListItem
                  nodeId={value.id}
                  key={value.id}
                  title={value?.alias ?? `Plan ${value.name}`}
                  onClick={() => onChange(value)}
                  rightIcon={
                    !readonly
                      ? renderActionsPopover(
                          <Icon
                            aria-label="more_options"
                            iconName="ellipsis-vertical"
                            iconStyle="fal"
                            size="sm"
                          />,
                          value
                        )
                      : undefined
                  }
                />
              )
            })}
          </TreeListItem>
        </>
      )
    }
  })

  const togglePopOver = () => {
    setIsPopoverOpen(!isPopoverOpen)
  }

  return (
    <div className={css.plan_container}>
      <span>Plan:</span>
      {!readonly && (
        <Tooltip2 content="New move plan" placement={Position.TOP}>
          <span className={css.new_plan} onClick={() => openDialog('AddGroupDialog')}>
            <Icon iconName={Icons.CALENDAR} iconStyle="fal" size="sm"></Icon>
          </span>
        </Tooltip2>
      )}
      <ButtonGroup style={{ width: '100%', height: '100%' }}>
        <Popover
          hasBackdrop
          onClose={() => renaming && setRenaming(null)}
          position={Position.BOTTOM_RIGHT}
          content={
            groups.length > 1 ? (
              <>
                <TreeList
                  style={{ width: 285, maxHeight: 410, overflowY: 'auto' }}
                  id="plan_selector">
                  {menuItems as unknown as JSX.Element}
                </TreeList>
                <Divider />
                {!readonly && (
                  <Flex justify="flex-end" direction="row" style={{ paddingBottom: '10px' }}>
                    <IButton
                      layout="danger"
                      size="small"
                      startIcon={<Icon iconName={Icons.TRASH} iconStyle="fal" />}
                      onClick={() => {
                        if (sldPlan?.assignmentPlans?.length) {
                          openDialog('DeletePlanGroupDialog', sldPlan)
                          setSldPlan(null)
                        } else {
                          return null
                        }
                      }}>
                      Delete All Plans
                    </IButton>
                    <IButton
                      layout="primary"
                      style={{ marginLeft: '10px', marginRight: '5px' }}
                      size="small"
                      onClick={() => openDialog('AddGroupDialog')}
                      startIcon={<Icon iconName={Icons.ADD} iconStyle="fal" />}>
                      New Plan
                    </IButton>
                  </Flex>
                )}
              </>
            ) : (
              <Flex
                align="center"
                justify="center"
                direction="column"
                style={{ height: 200, width: 300 }}>
                <p>You don't have any plans yet.</p>
                {!readonly && (
                  <IButton
                    layout="primary"
                    size="small"
                    startIcon={<Icon iconName={Icons.ADD} iconStyle="fal" />}
                    onClick={() => openDialog('AddGroupDialog')}>
                    New Plan
                  </IButton>
                )}
              </Flex>
            )
          }>
          <div className={css.selecte_plan_button} onClick={togglePopOver}>
            {selectedPlan ? apButtonLabel(selectedPlan) : 'Select'}
            <Icon
              iconName={isPopoverOpen ? 'chevron-up' : 'chevron-down'}
              iconStyle="fal"
              size="sm"></Icon>
          </div>
        </Popover>
      </ButtonGroup>
      {!readonly && (
        <>
          <NewPlanGroupDialog isOpen={activeDialog === 'AddGroupDialog'} onClose={closeDialog} />
          <NewPlanDialog
            isOpen={activeDialog === 'AddPlanDialog'}
            onClose={closeDialog}
            group={data}
          />
          <ClonePlanDialog
            isOpen={activeDialog === 'ClonePlanDialog'}
            onClose={closeDialog}
            plan={data}
          />
          <DeletePlanDialog
            isOpen={activeDialog === 'DeletePlanDialog'}
            onClose={closeDialog}
            plan={data}
            deletePlan={deletePlan}
          />
          <PromotePlanDialog
            isOpen={activeDialog === 'PromotePlanDialog'}
            onClose={closeDialog}
            plan={data}
          />
          <DeletePlanGroupDialog
            isOpen={activeDialog === 'DeletePlanGroupDialog'}
            onClose={closeDialog}
            group={data}
            deleteGroup={deleteGroup}
          />
        </>
      )}
      {renaming && (
        <AliasForm
          plan={sldPlan}
          onUpdate={(updatedPlan: IAssignmentPlan) => {
            setRenaming(null)
            updatePlan(updatedPlan)
          }}
          onCancel={() => setRenaming(null)}
          renaming={renaming}
        />
      )}
    </div>
  )
}

function AliasForm({
  plan,
  onCancel,
  onUpdate,
  renaming,
}: {
  plan: IAssignmentPlan
  onCancel: () => void
  onUpdate: (plan: IAssignmentPlan) => void
  renaming: string | null
}) {
  const [alias, setAlias] = useState(plan?.alias ?? '')
  const [loading, setLoading] = useState(false)
  const canSubmit = !loading && alias.trim() !== (plan?.alias ?? '')

  function commitUpdate() {
    if (canSubmit) {
      setLoading(true)
      api.ap
        .update({ id: plan?.id, alias })
        .then((updatedPlan) => {
          Toaster.success({
            message: alias.trim()
              ? `Name changed to "${alias}"`
              : `Name changed to Plan ${updatedPlan.name}`,
          })
          setLoading(false)
          onUpdate(updatedPlan)
        })
        .catch((error) => {
          Toaster.fail({ error })
          setLoading(false)
        })
    }
  }

  return (
    <Dialog isOpen={renaming === plan?.id}>
      <div className={css.plan_wrapper} onClick={(e) => e.stopPropagation()}>
        <form
          onSubmit={(e) => {
            e.preventDefault()
            commitUpdate()
          }}>
          <InputGroup
            fill
            autoFocus
            placeholder="Enter plan name..."
            disabled={loading}
            value={alias}
            onChange={(e: SyntheticEvent<HTMLInputElement>) => setAlias(e.currentTarget.value)}
          />
        </form>
        <section>
          <Flex justify="space-between">
            <IconButton
              minimal
              disabled={!canSubmit}
              tooltip="Save"
              iconName="save"
              onClick={commitUpdate}
            />
            <IconButton
              minimal
              disabled={loading}
              tooltip="Cancel"
              iconName="times"
              onClick={onCancel}
            />
          </Flex>
        </section>
      </div>
    </Dialog>
  )
}

function apName(plan: IAssignmentPlan) {
  if (plan.name === 'Current') {
    return 'Now'
  }

  return plan.alias || `Plan ${plan.name}`
}

function apButtonLabel(plan: IAssignmentPlan) {
  if (plan.name === 'Current') {
    return <Flex align="center">Now</Flex>
  }

  return (
    <div>
      {plan.alias || `Plan ${plan.name}`}
      <div style={{ fontSize: 10, color: 'var(--grey1)' }}>
        <PrettyDate utc value={plan.deployAt} />
      </div>
    </div>
  )
}

export function getPrimaryPlan(sGroups: IScenarioGroup[]) {
  let result: IAssignmentPlan | undefined

  sGroups.forEach((g) => {
    if (!result) {
      result = g.assignmentPlans.find((p) => p.name === 'Current')
    }
  })

  return result as IAssignmentPlan
}

export function getPlanById(sGroups: IScenarioGroup[], id: string) {
  let result: IAssignmentPlan | undefined

  sGroups.forEach((g) => {
    if (!result) {
      result = g.assignmentPlans.find((p) => p.id === id)
    }
  })

  return result as IAssignmentPlan
}
